import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIf, isPlatformBrowser } from '@angular/common';
import { MetaService } from '../../core/services/meta.service';
import { PageType, Path } from '../../core/path.enum';
import { FilmDetailService } from '../../core/services/film-detail.service';
import { Subject, takeUntil, timer } from 'rxjs';
import { Movie } from '../../core/interfaces/movie.interface';
import { WebSeriesSeasonResponse } from '../../core/interfaces/webseires.interface';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-middleware',
  standalone: true,
  imports: [NgIf],
  templateUrl: './middleware.component.html',
})
export class MiddlewareComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  message = '';
  isLoading = true;
  hasError = false;
  readonly productUrl = environment.productUrl;
  readonly logoPath = `${this.productUrl}${Path.ABC_LOGO}`;

  constructor(
    private readonly filmDetailService: FilmDetailService,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    private readonly metaService: MetaService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit(): void {
    this.activeRoute.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const id = params['id'];
        const path = this.activeRoute.snapshot.url[1]?.path;

        if (path === PageType.MOVIE_DETAIL) {
          this.setMovieMetaTags(id);
        } else if (path === PageType.WEB_SERIES) {
          this.setWebSeriesMetaTags(id);
        }
      });
  }

  // set movie meta tags
  setMovieMetaTags(id: string) {
    this.filmDetailService
      .getFilmDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { message: string; data?: Movie; status: number }) => {
          if (res.status === 200 && res.data) {
            this.isLoading = false;
            this.hasError = false;
            this.message = `Redirecting to Movie ${res.data.title}...`;
            this.metaService.setMetaTags({
              title: res.data.title,
              description: res.data.description,
              image: res.data.media.banner,
              mediaType: 'film',
              url: this.router.url,
            });

            const redirectUrl = `${environment.productUrl}/${this.router.url}`;

            timer(2000)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                if (isPlatformBrowser(this.platformId)) {
                  globalThis.window.location.replace(redirectUrl);
                }
              });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.hasError = true;
          this.message = err.error?.message || 'Movie not found';
        },
      });
  }

  // set web series meta tags
  setWebSeriesMetaTags(id: string) {
    this.filmDetailService
      .getWebSeriesDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: {
          message: string;
          data?: WebSeriesSeasonResponse;
          status: number;
        }) => {
          if (res.status === 200 && res.data) {
            this.isLoading = false;
            this.hasError = false;
            this.message = `Redirecting to Web Series ${res.data.season.title}...`;
            this.metaService.setMetaTags({
              title: res.data.season.title,
              description: res.data.season.description,
              image:
                res.data.season.media.banner ||
                res.data.season.media.largeBanner ||
                res.data.season.media.portrait ||
                '',
              mediaType: 'web-series',
              url: this.router.url,
            });

            const redirectUrl = `${environment.productUrl}/${this.router.url}`;

            timer(2000)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                if (isPlatformBrowser(this.platformId)) {
                  globalThis.window.location.replace(redirectUrl);
                }
              });
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.hasError = true;
          this.message = err.error?.message || 'Web series not found';
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
